import React from "react";
import { useSetAtom } from "jotai";
import { BugAntIcon, UserCircleIcon, WrenchScrewdriverIcon } from "@heroicons/react/24/outline";
import { BrandIcons, Flyout, Link, Translate } from "../../UI";
import { RouteConstants } from "../../../Routes";
import { errorReportSlideoverAtom, useLogin } from "../../../services";
import { isHighAccess } from "../../../utils";

const MenuIconStyles = "h-5 w-5 shrink-0 text-brand dark:text-brand-dark";

const AccountIcon = BrandIcons.account;
const SettingsIcon = BrandIcons.settings;
const SignOutIcon = BrandIcons['sign-out'];

const MenuItemStyles = "flex items-center gap-x-3 px-3 py-2 rounded-md text-[13px] font-semibold leading-5 dark:text-white text-black dark:hover:bg-stone-700 hover:bg-surface-strong cursor-pointer";

// #region HeaderUserMenu
/**
 * Account menu in the header's top-right — the replacement for the old
 * email-address button that sat at the bottom of the sidebar.
 *
 * The trigger is icon-only (brand logo when one exists, falling back to a user
 * glyph); the email now appears inside the panel instead of in the chrome, the
 * way a Google account menu behaves. The Language entry was removed entirely.
 */
export const HeaderUserMenu: React.FunctionComponent = (): JSX.Element => {
    const [user, , loginKeyFunctions] = useLogin();
    const closeRef = React.useRef<(() => void) | null>(null);
    const setErrorReportOpen = useSetAtom(errorReportSlideoverAtom);

    const close = () => closeRef.current?.();

    const fullName = [user?.first, user?.last].filter(Boolean).join(' ');

    return (
        <Flyout
            title=""
            icon={UserCircleIcon}
            closeRef={closeRef}
            buttonClassName="!px-1.5 !py-1.5"
            panelClassName="w-72"
            hideArrow={true}
            buttonContent={<AccountIcon className="h-6 w-6 shrink-0 text-brand dark:text-brand-dark" />}
        >
            {/* Identity block — who you are signed in as. */}
            <li key="identity" className="px-3 pt-2 pb-3 mb-1 border-b dark:border-stone-500 border-stone-200">
                <div className="text-[13px] font-semibold leading-5 dark:text-white text-black truncate">
                    {fullName || user?.email}
                </div>
                {fullName ? (
                    <div className="text-xs leading-5 dark:text-gray-300 text-gray-500 truncate">
                        {user?.email}
                    </div>
                ) : null}
            </li>

            <li key="settings">
                <Link href={RouteConstants.SETTINGS_HOME} action={close} className={MenuItemStyles}>
                    <SettingsIcon className={MenuIconStyles} />
                    <span><Translate translation_key="NAVIGATION.SETTINGS" fallback="Settings" /></span>
                </Link>
            </li>

            <li key="report-error">
                <button
                    type="button"
                    onClick={() => {
                        close();
                        setErrorReportOpen(true);
                    }}
                    className={`${MenuItemStyles} w-full text-left`}
                >
                    <BugAntIcon className={MenuIconStyles} />
                    <span>Report an Error</span>
                </button>
            </li>

            {isHighAccess(user) ? (
                <li key="admintools">
                    <Link href={RouteConstants.ADMIN_TOOLS} action={close} className={MenuItemStyles}>
                        <WrenchScrewdriverIcon className={MenuIconStyles} />
                        <span>Admin Tools</span>
                    </Link>
                </li>
            ) : null}

            <li key="logout">
                <Link
                    href=""
                    action={() => {
                        close();
                        loginKeyFunctions.logout();
                    }}
                    className={MenuItemStyles}
                >
                    <SignOutIcon className={MenuIconStyles} />
                    <span><Translate translation_key="NAVIGATION.LOGOUT" fallback="Sign Out" /></span>
                </Link>
            </li>
        </Flyout>
    );
};
